import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useCart } from '@/App';
import { toast } from 'sonner';
import { ShoppingCart, Shield, Truck, ArrowLeft, Minus, Plus, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export default function ProductDetailPage() {
  const { productId } = useParams();
  const { addToCart, loading: cartLoading } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${productId}`);
        setProduct(response.data);
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error('Product not found');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  const handleAddToCart = async () => {
    try {
      await addToCart(product.product_id, quantity);
      toast.success(`${product.name} added to cart!`);
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  const sportColors = {
    football: 'bg-green-600',
    basketball: 'bg-orange-500',
    baseball: 'bg-blue-600',
    soccer: 'bg-purple-600',
    multi: 'bg-[#C41E3A]'
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C41E3A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pt-20 min-h-screen flex flex-col items-center justify-center">
        <h2 className="heading-display text-3xl text-white mb-4">Product Not Found</h2>
        <Link to="/products">
          <Button className="btn-primary">Back to Products</Button>
        </Link>
      </div>
    );
  }

  const discount = product.original_price
    ? Math.round((1 - product.price / product.original_price) * 100)
    : null;

  return (
    <div className="pt-20 min-h-screen" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="border-b border-[#27272A] py-4">
        <div className="container-main">
          <Link to="/products" className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors duration-200" data-testid="back-to-products">
            <ArrowLeft className="w-4 h-4" />
            Back to Products
          </Link>
        </div>
      </div>

      {/* Product Section */}
      <section className="section-gap">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Images */}
            <div className="space-y-4">
              {/* Main Image */}
              <div className="aspect-square bg-[#0A0A0A] border border-[#27272A] overflow-hidden">
                <img
                  src={product.images?.[selectedImage] || 'https://images.pexels.com/photos/7783409/pexels-photo-7783409.jpeg'}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  data-testid="product-main-image"
                />
              </div>

              {/* Thumbnails */}
              {product.images?.length > 1 && (
                <div className="flex gap-4">
                  {product.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`w-20 h-20 border overflow-hidden transition-colors duration-200 ${
                        selectedImage === index ? 'border-[#C41E3A]' : 'border-[#27272A]'
                      }`}
                    >
                      <img src={image} alt={`${product.name} ${index + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              {/* Badges */}
              <div className="flex flex-wrap gap-2">
                <Badge className={`${sportColors[product.sport] || 'bg-[#27272A]'} text-white font-barlow uppercase text-xs px-3 py-1`}>
                  {product.sport}
                </Badge>
                <Badge className="bg-[#27272A] text-white font-barlow uppercase text-xs px-3 py-1">
                  {product.category?.replace('_', ' ')}
                </Badge>
                {product.authentication && (
                  <Badge className="bg-[#007AFF] text-white font-barlow uppercase text-xs px-3 py-1">
                    {product.authentication} Certified
                  </Badge>
                )}
                {product.featured && (
                  <Badge className="bg-[#C41E3A] text-white font-barlow uppercase text-xs px-3 py-1">
                    Featured
                  </Badge>
                )}
              </div>

              {/* Title */}
              <h1 className="heading-display text-3xl md:text-4xl text-white" data-testid="product-title">
                {product.name}
              </h1>

              {/* Athlete & Team */}
              {(product.athlete || product.team) && (
                <p className="text-white/60 text-lg">
                  {product.athlete}{product.athlete && product.team && ' • '}{product.team}
                </p>
              )}

              {/* Price */}
              <div className="flex items-center gap-4">
                <span className="font-barlow font-black text-4xl text-white" data-testid="product-price">
                  ${product.price.toFixed(2)}
                </span>
                {product.original_price && (
                  <>
                    <span className="text-white/40 text-xl line-through">
                      ${product.original_price.toFixed(2)}
                    </span>
                    <Badge className="bg-[#C41E3A] text-white font-barlow uppercase text-sm px-3 py-1">
                      Save {discount}%
                    </Badge>
                  </>
                )}
              </div>

              {/* Description */}
              <p className="text-white/70 leading-relaxed" data-testid="product-description">
                {product.description}
              </p>

              {/* Stock Status */}
              <div className="flex items-center gap-2">
                {product.in_stock ? (
                  <>
                    <Check className="w-5 h-5 text-green-500" />
                    <span className="text-green-500 font-medium">In Stock ({product.stock_quantity} available)</span>
                  </>
                ) : (
                  <span className="text-[#C41E3A] font-bold uppercase">Sold Out</span>
                )}
              </div>

              {/* Quantity & Add to Cart */}
              {product.in_stock && (
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Quantity */}
                  <div className="flex items-center border border-[#27272A]">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="p-3 hover:bg-white/5 transition-colors duration-200"
                      data-testid="decrease-quantity"
                    >
                      <Minus className="w-5 h-5 text-white" />
                    </button>
                    <span className="px-6 font-barlow font-bold text-white" data-testid="quantity-display">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(Math.min(product.stock_quantity, quantity + 1))}
                      className="p-3 hover:bg-white/5 transition-colors duration-200"
                      data-testid="increase-quantity"
                    >
                      <Plus className="w-5 h-5 text-white" />
                    </button>
                  </div>

                  {/* Add to Cart */}
                  <Button
                    onClick={handleAddToCart}
                    disabled={cartLoading}
                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                    data-testid="add-to-cart-btn"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    Add to Cart
                  </Button>
                </div>
              )}

              {/* Trust Badges */}
              <div className="pt-6 border-t border-[#27272A] space-y-4">
                <div className="flex items-center gap-4">
                  <Shield className="w-6 h-6 text-[#C41E3A]" />
                  <div>
                    <h4 className="font-barlow font-bold text-white">Authenticity Guarantee</h4>
                    <p className="text-white/50 text-sm">
                      {product.authentication ? `Verified by ${product.authentication}` : 'COA Included'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Truck className="w-6 h-6 text-[#C41E3A]" />
                  <div>
                    <h4 className="font-barlow font-bold text-white">Free Shipping</h4>
                    <p className="text-white/50 text-sm">On orders over $250 (U.S. only)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
